import {InvoiceResponse} from '@/types/service-response'
import {InvoicePayment} from '@/model/InvoicePayment'
import {InvoicePaymentStatus} from '@/types/enums'
import moment from 'moment'
import {TranslateResult} from 'vue-i18n'
import i18n from '@/plugins/i18n'
import {formatBoarderBankIdentificationCode, formatIban, formatIntPrice} from '@/services/formatter';

export class Invoice {
  private constructor(
    readonly id: string,
    readonly customer: string,
    readonly issueDate: Date,
    readonly dueDate: Date,
    readonly iban: string,
    readonly boarderBankIdentificationCode: number,
    readonly specificSymbol: string,
    readonly constantSymbol: string,
    readonly periodKind: string,
    readonly name: string,
    readonly currency: string,
    readonly priceItemSum: number,
    readonly amountFromWallet: number,
    readonly amountFromDept: number,
    readonly priceTotal: number,
    readonly invoicePayments: InvoicePayment[],
    readonly periodStart: Date,
    readonly periodEnd: Date,
    readonly paymentStatus: InvoicePaymentStatus
  ) {
  }

  get isLate(): boolean {
    return this.dueDate <= new Date()
  }

  get paymentStatusColor(): string {
    let color = 'orange'

    if (this.paymentStatus === InvoicePaymentStatus.notPaid) {
      color = this.isLate ? 'red' : 'orange'
    } else if (this.paymentStatus === InvoicePaymentStatus.fullyPaid) {
      color = 'green'
    } else if (this.paymentStatus === InvoicePaymentStatus.partiallyPaid) {
      color = 'orange'
    }

    return color
  }

  get paymentStatusIcon(): string {
    let icon = 'far fa-question-circle'

    if (this.paymentStatus === InvoicePaymentStatus.notPaid) {
      icon = this.isLate ? 'far fa-exclamation-triangle' : 'far fa-exclamation-circle'
    } else if (this.paymentStatus === InvoicePaymentStatus.fullyPaid) {
      icon = 'far fa-check-circle'
    } else if (this.paymentStatus === InvoicePaymentStatus.partiallyPaid) {
      icon = 'far fa-exclamation-triangle'
    }

    return icon
  }

  get paymentStatusLabel(): TranslateResult {
    let label: TranslateResult = i18n.t('billingPage.notPayed');

    if (this.paymentStatus === InvoicePaymentStatus.notPaid) {
      label = this.isLate ? i18n.t('billingPage.notPayedAfterExpiration') : i18n.t('billingPage.notPayed')
    } else if (this.paymentStatus === InvoicePaymentStatus.fullyPaid) {
      label = i18n.t('billingPage.fullyPayed')
    } else if (this.paymentStatus === InvoicePaymentStatus.partiallyPaid) {
      label = i18n.t('billingPage.partiallyPayed')
    }

    return label
  }

  get formattedPeriodRange(): string {
    const startFormat = this.periodStart.getFullYear() !== this.periodEnd.getFullYear() ? 'D.M.YYYY' : 'D.M'
    const start = moment(this.periodStart).format(startFormat)
    const end = moment(this.periodEnd).format('D.M.YYYY')

    return `${start} - ${end}`
  }

  get formattedPriceItemSum(): string {
    return formatIntPrice(this.priceItemSum)
  }

  get formattedPriceTotal(): string {
    return formatIntPrice(this.priceTotal)
  }

  get formattedAmountFromDept(): string {
    return formatIntPrice(this.amountFromDept)
  }

  get formattedAmountFromWallet(): string {
    return formatIntPrice(this.amountFromWallet)
  }

  get formattedIban(): string {
    return formatIban(this.iban)
  }

  get formattedBoarderBankIdentificationCode(): string {
    return formatBoarderBankIdentificationCode(this.boarderBankIdentificationCode)
  }

  static createFromResponse(item: InvoiceResponse): Invoice {
    return new Invoice(
      item.id,
      item.customer,
      new Date(item.issueDate),
      new Date(item.dueDate),
      item.iban,
      item.boarderBankIdentificationCode,
      item.specificSymbol,
      item.constantSymbol,
      item.periodKind,
      item.name,
      item.currency,
      item.priceItemSum,
      item.amountFromWallet,
      item.amountFromDept,
      item.priceTotal,
      item.invoicePayments && InvoicePayment.createFromResponse(item.invoicePayments) || [],
      new Date(item.periodStart),
      new Date(item.periodEnd),
      Invoice.resolvePaymentStatus(item.paymentStatus),
    )
  }

  static createListFromResponse(invoiceList: InvoiceResponse[]): Invoice[] {
    return invoiceList.map(invoiceItem => Invoice.createFromResponse(invoiceItem))
  }

  private static resolvePaymentStatus(statusFromResponse: string): InvoicePaymentStatus {
    const statusMap: { [key: string]: InvoicePaymentStatus } = {
      'not-paid': InvoicePaymentStatus.notPaid,
      'fully-paid': InvoicePaymentStatus.fullyPaid,
      'partially-paid': InvoicePaymentStatus.partiallyPaid
    }

    return statusMap[statusFromResponse] || InvoicePaymentStatus.notDetermined
  }
}
